import React, { useEffect, useState } from 'react';
import {
  LIVE_SESSION_HEADER_COPY,
  LIVE_SESSION_HEADER_LABEL,
  LIVE_SESSION_HEADER_TICK_MS,
  LIVE_VIEWER_ROLE,
  LiveViewerRole,
} from '../constants/liveSessionHeader';
import { elapsedSeconds, formatElapsedClock, formatStartClock } from '../utils/liveSessionClock';

interface LiveSessionHeaderProps {
  /** The live call's name, when known. */
  sessionName: string | null;
  /** When the call started, read on every tick: it is set once the bot is in the call. */
  getCallStartTime: () => Date | null;
  /** Who the user is on the call: row 1 reads "Live", "Live · Co-Seller" or "Live · Observer". */
  viewerRole?: LiveViewerRole;
}

/** The current time, refreshed every tick while mounted. */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), LIVE_SESSION_HEADER_TICK_MS);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

/**
 * The live session header (owner, 2026-10-07), the same two rows as macOS
 * SessionStatusView and the WPF status pill:
 *   (● Live) : {session name}          a green pill, then the name bold in the theme colour
 *   Started at: 10:14 AM · 12:34       once the call's start is known
 * A co-seller's pill reads "Live · Co-Seller" and an observer's
 * "Live · Observer"; the owner's is unchanged. The dot is green. It sits in
 * the session status card (ConversationPage).
 */
const LiveSessionHeader: React.FC<LiveSessionHeaderProps> = ({
  sessionName,
  getCallStartTime,
  viewerRole = LIVE_VIEWER_ROLE.owner,
}) => {
  const now = useNow();
  const start = getCallStartTime();
  return (
    <div className="min-w-0 space-y-1" data-testid="live-session-header">
      <p className="flex min-w-0 items-center">
        <span
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-[3px] text-xs font-semibold text-success"
          style={{ backgroundColor: 'color-mix(in srgb, var(--success) 12%, transparent)' }}
        >
          <span
            className="h-2 w-2 shrink-0 rounded-full bg-success"
            aria-hidden="true"
            data-testid="live-session-dot"
          />
          {LIVE_SESSION_HEADER_LABEL[viewerRole]}
        </span>
        {sessionName ? (
          <span className="min-w-0 truncate" title={sessionName}>
            <span className="whitespace-pre text-xs font-semibold text-ink">{LIVE_SESSION_HEADER_COPY.nameSeparator}</span>
            <span className="text-[13px] font-bold text-signature">{sessionName}</span>
          </span>
        ) : null}
      </p>
      {start ? (
        <p className="text-[11px] text-ink-secondary">
          {`${LIVE_SESSION_HEADER_COPY.startedAt} ${formatStartClock(start)}${LIVE_SESSION_HEADER_COPY.elapsedSeparator}${formatElapsedClock(elapsedSeconds(start, now))}`}
        </p>
      ) : null}
    </div>
  );
};

export default LiveSessionHeader;
